import { createHttpLink } from '@apollo/client';

import {
  GRAPHQL_ENDPOINT,
  GRAPHQL_PUBLIC_ENDPOINT,
  DASHBOARD_ENDPOINT,
} from '../../../config.json';

// Capture the real fetch BEFORE any global patch is applied.
// mojibakedFetch always calls this reference, never window.fetch,
// so patching window.fetch = mojibakedFetch causes no recursion.
const _realFetch: typeof fetch = typeof window !== 'undefined'
  ? window.fetch.bind(window)
  : fetch;

/**
 * Handles Scenario B mojibake: server sends raw Latin-1 bytes in the JSON body
 * (e.g. 0xCA for Ê). UTF-8 JSON parser replaces invalid sequences with U+FFFD,
 * losing the original byte irreversibly at the string level.
 *
 * Fix: read response as ArrayBuffer, decode UTF-8, re-decode windows-1252 if
 * U+FFFD detected. Applied globally via window.fetch patch in client.tsx because
 * Apollo Client 3.x ignores the createHttpLink `fetch` option in some code paths.
 */
export async function mojibakedFetch(
  input: RequestInfo,
  init?: RequestInit,
): Promise<Response> {
  const response = await _realFetch(input, init);
  const buffer = await response.arrayBuffer();

  const utf8Text = new TextDecoder('utf-8').decode(buffer);
  const hasFFFD = utf8Text.includes('\uFFFD');

  const text = hasFFFD
    ? new TextDecoder('windows-1252').decode(buffer)
    : utf8Text;

  return new Response(text, {
    status: response.status,
    statusText: response.statusText,
    headers: { 'Content-Type': response.headers.get('Content-Type') ?? 'application/json' },
  });
}

export const httpPrivateLink = createHttpLink({ uri: GRAPHQL_ENDPOINT });
export const httpPublicLink = createHttpLink({ uri: GRAPHQL_PUBLIC_ENDPOINT });
export const httpDashboardLink = createHttpLink({ uri: DASHBOARD_ENDPOINT });
